import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Minus, Plus, Trash2, ArrowRight, AlertTriangle } from 'lucide-react';
import { SheetHeader, SheetTitle, SheetClose } from '../components/ui/sheet';
import { STORE_CONFIG } from '../config/storeConfig';

const CartDrawer = () => {
  const { items, removeItem, updateQuantity, getTotal, clearCart } = useCart();

  if (items.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <SheetHeader className="p-6 border-b border-white/10">
          <SheetTitle className="font-heading text-2xl text-white">Your Cart</SheetTitle>
        </SheetHeader>
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <p className="text-neutral-400 font-mono text-sm uppercase tracking-widest mb-6">
            Your cart is empty
          </p>
          <SheetClose asChild>
            <Link
              to="/shop"
              data-testid="continue-shopping-link"
              className="bg-cyan-400 text-white px-6 py-3 font-mono uppercase tracking-widest text-sm hover:bg-cyan-400-hover transition-colors"
            >
              Start Shopping
            </Link>
          </SheetClose>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <SheetHeader className="p-6 border-b border-white/10">
        <SheetTitle className="font-heading text-2xl text-white">
          Your Cart ({items.length})
        </SheetTitle>
      </SheetHeader>

      {/* Cart Items */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {items.map((item, index) => (
          <div
            key={`${item.product.id}-${index}`}
            data-testid={`cart-item-${item.product.id}`}
            className="flex gap-4 pb-6 border-b border-white/5"
          >
            <img
              src={item.product.images[0]?.url}
              alt={item.product.title}
              className="w-24 h-24 object-cover bg-void-subtle"
            />
            <div className="flex-1">
              <h4 className="font-heading text-sm font-semibold text-white mb-1">
                {item.product.title}
              </h4>
              {item.product.colorPreference && (
                <p className="text-xs text-cyan-400 mb-1">
                  Color: {item.product.colorPreference}
                </p>
              )}
              <p className="text-cyan-400 font-mono text-sm mb-3">
                ${item.product.price.toFixed(2)}
              </p>
              <div className="flex items-center gap-3">
                <button
                  data-testid={`decrease-qty-${item.product.id}`}
                  onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                  className="w-8 h-8 border border-white/20 flex items-center justify-center text-neutral-400 hover:text-white hover:border-white/40 transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="font-mono text-sm text-white w-8 text-center">
                  {item.quantity}
                </span>
                <button
                  data-testid={`increase-qty-${item.product.id}`}
                  onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                  className="w-8 h-8 border border-white/20 flex items-center justify-center text-neutral-400 hover:text-white hover:border-white/40 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  data-testid={`remove-item-${item.product.id}`}
                  onClick={() => removeItem(item.product.id)}
                  className="ml-auto text-neutral-500 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Cart Footer */}
      <div className="p-6 border-t border-white/10 space-y-4">
        <div className="flex justify-between items-center">
          <span className="font-mono text-sm uppercase tracking-widest text-neutral-400">
            Subtotal
          </span>
          <span className="font-heading text-2xl text-white">
            ${getTotal().toFixed(2)}
          </span>
        </div>
        <p className="text-xs text-neutral-500">
          Shipping calculated at checkout
        </p>
        
        {/* Store Paused Warning */}
        {!STORE_CONFIG.STORE_OPEN && (
          <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <p className="text-amber-400 text-xs">{STORE_CONFIG.PAUSED_BUTTON_TEXT}</p>
          </div>
        )}
        
        <SheetClose asChild>
          {STORE_CONFIG.STORE_OPEN ? (
            <Link
              to="/checkout"
              data-testid="checkout-button"
              className="w-full bg-cyan-400 text-white py-4 font-mono uppercase tracking-widest text-sm hover:bg-cyan-400-hover transition-colors flex items-center justify-center gap-2"
            >
              Checkout <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <button
              data-testid="checkout-button-disabled"
              disabled
              className="w-full bg-neutral-700 text-neutral-400 py-4 font-mono uppercase tracking-widest text-sm cursor-not-allowed flex items-center justify-center gap-2"
            >
              {STORE_CONFIG.PAUSED_BUTTON_TEXT}
            </button>
          )}
        </SheetClose>
        <button
          data-testid="clear-cart-button"
          onClick={clearCart}
          className="w-full text-neutral-500 hover:text-white text-sm font-mono uppercase tracking-widest transition-colors"
        >
          Clear Cart
        </button>
      </div>
    </div>
  );
};

export default CartDrawer;
